import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Policy } from './Policy';
import { FlatExtra } from './FlatExtra';
import { Utils } from '../Utils';
import { PolicyAmendmentProperty } from '../static/PolicyAmendmentProperty';
import { FlatExtraAmendments } from '../static/FlatExtraAmendments';

@supertypeClass({ toClient: true })
export class PremiumBreakdown extends Everything {
  /**
   * Use 0.08333333333 (instead of 1/12) for this ratio,
   * because 0.08333333333 factor is used by product and policy admin.
   * https://massmutual.atlassian.net/browse/HL-10302
   */
  static YEARLY_TO_MONTHLY_RATIO = 0.08333333333;

  static getTotalFlatExtras = function (flatExtras) {
    const total = flatExtras.reduce((sum, flatExtra) => {
      return sum + flatExtra.calculateMonthlyAmount();
    }, 0);

    return Number(Utils.round(total, 2).toFixed(2));
  };

  static getTotalUnitFlatExtras(flatExtras) {
    const total = flatExtras.reduce(
      function (sum, flatExtra) {
        return sum + flatExtra.amount;
      }.bind(this),
      0
    );

    return Number(Utils.round(total, 2).toFixed(2));
  }

  @property({
    comment: 'total death benefit premium, frequency is in policy',
  })
  base: number;

  @property({
    persist: false,
    comment: 'total annual death benefit premium',
  })
  baseAnnual: number;

  @property({
    persist: false,
    comment:
      'total death benefit premium (with Extended Period of Convertibility), frequency is in policy. "base" property will always reflect current premium and will = baseWithECP if policy.hasEcp and will = baseWithoutECP if !policy.hasEcp',
  })
  baseWithECP: number | null;

  @property({
    persist: false,
    comment:
      'total death benefit premium (without Extended Period of Convertibility), frequency is in policy. "base" property will always reflect current premium and will = baseWithECP if policy.hasEcp and will = baseWithoutECP if !policy.hasEcp',
  })
  baseWithoutECP: number;

  @property({
    comment: 'total waiver premium, frequency is in policy',
  })
  waiver: number;

  @property({
    persist: false,
    comment:
      'Waiver of premium amount. This is always set to a value != 0 if eligible for WOP. "waiver" property = 0 if !policy.waiver, but this will not. If not eligible for WOP, this will not have a value',
  })
  waiverOfPremiumAmount: number | null;

  @property({ type: Array, getType: () => FlatExtra })
  flatExtras: Array<FlatExtra> = [];

  @property({ getType: () => Policy })
  policy: Policy;

  constructor() {
    super();
  }

  static new(base, waiver, flatExtras, policy) {
    return new PremiumBreakdown().init(base, waiver, flatExtras, policy);
  }

  init(base, waiver, flatExtras, policy) {
    this.policy = policy;

    if (typeof base !== 'undefined') {
      this.update(base, waiver, flatExtras);
    }
    return this;
  }

  update(base: number, waiver: number, flatExtras: Array<FlatExtra>) {
    this.updateBase(base);
    this.waiver = this.policy.waiver ? Utils.round(waiver, 2) : 0;

    this.flatExtras = (flatExtras || []).map(
      function (fe) {
        return FlatExtra.new(fe.name, fe.amount, this.policy, this, fe.ruleCode, fe.premium, fe.expiryAfterInYears);
      }.bind(this)
    );
  }

  updateBase(base: number): void {
    this.base = base;
  }

  getTotal(): number {
    return Utils.round(
      this.base +
        this.policy.getTotalWaiverPremiumForRiders(this) +
        this.policy.getTotalBasePremiumForRiders() +
        this.getTotalFlatExtras() +
        this.policy.getTotalFlatExtrasForRiders(),
      2
    );
  }

  getTotalWithPendingAmendment(): number {
    return Utils.round(
      this.baseWithPendingAmendment() +
        this.policy.getTotalWaiverPremiumForRidersWithPendingAmendment() +
        this.policy.getTotalBasePremiumForRidersWithPendingAmendment() +
        this.flatExtraTotalWithPendingAmendment() +
        this.policy.getTotalFlatExtrasForRidersWithPendingAmendment(),
      2
    );
  }

  getTotalWithPendingPremiumAmendment(): number {
    return Utils.round(
      this.baseWithPendingPremiumAmendment() +
        this.policy.getTotalWaiverPremiumForRidersWithPendingAmendment() +
        this.policy.getTotalBasePremiumForRidersWithPendingAmendment() +
        this.flatExtraTotalWithPendingAmendment() +
        this.policy.getTotalFlatExtrasForRidersWithPendingAmendment(),
      2
    );
  }

  getCurrentTotal(): number {
    return Utils.round(this.base + this.waiver + this.getCurrentTotalFlatExtras(), 2);
  }

  getTlicTotal() {
    const total = this.getTotal();

    return total.toFixed(2);
  }

  getTotalFlatExtras() {
    return PremiumBreakdown.getTotalFlatExtras(this.flatExtras);
  }

  getCurrentTotalFlatExtras() {
    return PremiumBreakdown.getTotalFlatExtras(this.getCurrentFlatExtras());
  }

  getTotalUnitFlatExtras() {
    return PremiumBreakdown.getTotalUnitFlatExtras(this.flatExtras);
  }

  getTotalTempFlatExtrasPremium() {
    const total = this.getTempFlatExtras().reduce(
      function (sum, flatExtra) {
        return sum + flatExtra.premium;
      }.bind(this),
      0
    );

    return Number(Utils.round(total, 2).toFixed(2));
  }

  hasFlatExtras() {
    return this.flatExtras.length > 0;
  }

  getCurrentFlatExtras() {
    if (!this.policy.effectiveDate) {
      return this.flatExtras;
    }
    const elapsedYears = Math.floor((Date.now() - this.policy.effectiveDate.getTime()) / (365 * 24 * 60 * 60 * 1000));
    return this.flatExtras.filter((fe) => {
      return !fe.expiryAfterInYears || elapsedYears < fe.expiryAfterInYears;
    });
  }

  getTempFlatExtras() {
    return this.flatExtras.filter((fe) => {
      return fe.expiryAfterInYears;
    });
  }

  baseWithPendingAmendment(): number {
    const basePCH = this.policy.getPendingPolicyChange(PolicyAmendmentProperty.BASE);
    return basePCH ? (basePCH.getTypedNewValue() as number) : this.base;
  }

  baseWithPendingPremiumAmendment(): number {
    const basePCH = this.policy.getPendingPolicyChange(PolicyAmendmentProperty.BASE);
    const basePremiumPCH = this.policy.getPendingPolicyChange('premiumBreakdown.base');
    if (basePCH) {
      return basePCH.getTypedNewValue() as number;
    }
    return basePremiumPCH ? (basePremiumPCH.getTypedNewValue() as number) : this.base;
  }

  flatExtraTotalWithPendingAmendment(): number {
    return PremiumBreakdown.getTotalFlatExtras(FlatExtraAmendments.fetchFlatExtrasWithPendingAmendment(this.policy));
  }

  flatExtrasWithPendingAmendment(): Array<FlatExtra> {
    return FlatExtraAmendments.fetchFlatExtrasWithPendingAmendment(this.policy);
  }

  waiverAmountWithPendingAmendment(): number {
    //We have to consider two things:
    // a) is there an amendment to turn off WOP, and
    // b) was there an amendment that impacted waiver amount (e.g., face)

    //And, to make this even more confusing, there's mixed usage of "waiver" in our data model:
    //PremiumBreakdown.waiver is the dollar amount; Policy.waiver is a boolean to indicate if it was selected

    let hasWaiver: boolean;
    let waiverAmount: number;

    const hasWaiverPCH = this.policy.getPendingPolicyChange(PolicyAmendmentProperty.WAIVER);
    if (hasWaiverPCH) {
      hasWaiver = hasWaiverPCH.getTypedNewValue() as boolean;
    } else {
      hasWaiver = this.policy.waiver;
    }

    const waiverAmountPCH = this.policy.getPendingPolicyChange(PolicyAmendmentProperty.WAIVER_AMOUNT);
    if (waiverAmountPCH) {
      waiverAmount = waiverAmountPCH.getTypedNewValue() as number;
    } else {
      waiverAmount = this.waiver;
    }

    return hasWaiver ? waiverAmount : 0;
  }

  getAllAggregateFlatExtrasWithPendingAmendment(): Array<FlatExtra> {
    const aggFlatExtras = [];
    let rtrFlatExtras = [];
    if (this.policy.hasRtrRiderWithPendingAmendment()) {
      rtrFlatExtras = this.policy.rtrRider.flatExtrasWithPendingAmendment();
    }

    const flatExtras = FlatExtraAmendments.fetchFlatExtrasWithPendingAmendment(this.policy);
    flatExtras.forEach((fe) => {
      const rtrFE = rtrFlatExtras.find((rFE) => rFE.ruleCode === fe.ruleCode);
      aggFlatExtras.push(
        FlatExtra.new(
          fe.name,
          fe.amount,
          null,
          null,
          fe.ruleCode,
          fe.premium + (rtrFE ? rtrFE.premium : 0),
          fe.expiryAfterInYears,
          null
        )
      );
    });

    return aggFlatExtras;
  }
}

@supertypeClass({ toClient: true })
export class TLICPremiumBreakdown extends PremiumBreakdown {
  constructor() {
    super();
  }

  static new(base, waiver, flatExtras, policy) {
    return new TLICPremiumBreakdown().init(base, waiver, flatExtras, policy);
  }

  init(base, waiver, flatExtras, policy) {
    super.init(base, waiver, flatExtras, policy);
    return this;
  }

  /**
   * Always charge one month of premium for TLIC, even if policy payment frequency is annual.
   * @param base: base premium of this policy
   */
  updateBase(base: number): void {
    this.base = this.policy.isPolicyPaymentFrequencyAnnual() ? base * PremiumBreakdown.YEARLY_TO_MONTHLY_RATIO : base;
  }

  getTotalFlatExtras(): number {
    return PremiumBreakdown.getTotalFlatExtras(this.flatExtras);
  }

  /**
   * @return {Number} if the policy payment frequency is annual use the base and monthly flat extras.
   * else use the base and annual flat extras
   */
  getTotal(): number {
    if (this.policy.isPolicyPaymentFrequencyAnnual()) {
      return Utils.round(this.base + this.getTotalFlatExtras() * PremiumBreakdown.YEARLY_TO_MONTHLY_RATIO, 2);
    }
    return Utils.round(this.base + this.getTotalFlatExtras(), 2);
  }
}
